import {
  RESIDENCE_CALL_SCRIPT_QUESTION_TEXT,
  type ResidenceQuestionFieldKey,
  type ResidenceVerifiedFields,
} from "./callScriptResidence.types";
import { mergeOptionalNumber, mergeOptionalString, toYesNo } from "./callScriptShared.payload";
import type { CallScriptApplicantRole, CallScriptSaveWarning } from "./callScriptShared.types";

// ---------------------------------------------------------------------------
// callScriptResidence.savePayload
//
// Builds the POST body for the residence ("Customer Verification") Call
// Script save. Mirrors callScript.savePayload.ts (the employment equivalent):
// a non-blank "Verified Information" value wins, otherwise the saved value is
// sent, so an unrelated field the analyst never touched isn't blanked out
// server-side; a field with neither is left `undefined` and omitted from the
// JSON entirely.
//
// PROVISIONAL CONTRACT: no BFF endpoint or swagger exists for this script yet
// (see callScriptResidence.types.ts). The field names below follow the
// employment body's conventions plus CAPS2's own naming. Reconcile against the
// real swagger before this goes anywhere near a live environment.
// ---------------------------------------------------------------------------

/** One question's verified-vs-stored answer comparison. */
export interface ResidenceCallScriptVerificationAnswer {
  /** Absent until a residence question catalog endpoint exists — see ResidenceCallScriptHistoryAnswer. */
  verificationQuestionPk?: number;
  questionText: string;
  answerEntered: string;
  answerStored: string;
  isAnswerMatch: "Y" | "N";
}

export type ResidenceCallScriptApplicantRole = CallScriptApplicantRole;
export type ResidenceCallScriptSaveWarning = CallScriptSaveWarning;

/** Request body for the residence Call Script save (POST) endpoint. */
export interface ResidenceCallScriptSaveBody {
  applicantRole: CallScriptApplicantRole;
  applicantAddressPk?: number;
  /** Contact block. */
  phoneNumberDialed: string;
  isPhoneNoBad: "Y" | "N";
  dateOfBirth?: string;
  primaryNumber?: string;
  secondaryNumber?: string;
  /** The side panel's "does not want to be contacted at Primary Number" checkbox. */
  isDoNotContactPrimary: "Y" | "N";
  isEContractConsent?: "Y" | "N";
  isSignedCopyProvided?: "Y" | "N";
  /** Physical address. */
  physicalStreetNumber?: string;
  physicalStreetName?: string;
  physicalApartmentNumber?: string;
  physicalCity?: string;
  physicalState?: string;
  physicalZip?: string;
  /** Mailing address. */
  mailingStreetNumber?: string;
  mailingStreetName?: string;
  mailingApartmentNumber?: string;
  mailingCity?: string;
  mailingState?: string;
  mailingZip?: string;
  /** Deal / down payment block. */
  whoIsPrimaryDriver?: string;
  isTradeIn?: "Y" | "N";
  totalCashAndTradeInPaidOnDelivery?: number;
  isAnyAmountStillOwed?: "Y" | "N";
  remainingAmountDueDate?: string;
  grossTradeInAmount?: number;
  tradeInPayoffAmount?: number;
  cashDownPaidOnDelivery?: number;
  amountDueAfterDelivery?: number;
  finalAmountDueDate?: string;
  noteText: string;
  verificationAnswers: ResidenceCallScriptVerificationAnswer[];
}

/** Response from the residence Call Script save — persisted identifiers only, no full entry. */
export interface ResidenceCallScriptSaveResponse {
  verificationPhoneCallPk: number;
  verificationAnswerPks: number[];
  dpscNotePk?: number;
  occurredAt: string;
  warnings?: CallScriptSaveWarning[];
}

/** Everything the payload builder needs beyond the ids in the URL path. */
export interface ResidenceCallScriptSaveContext {
  personPath: "driver" | "secondSigner";
  applicantAddressPk?: number;
  note: string;
  doNotContactPrimary: boolean;
  verifiedFields: ResidenceVerifiedFields;
  /** The "Saved Information" column — the fallback for any field left blank on the call. */
  savedFields: ResidenceVerifiedFields;
}

/**
 * Maps a `"" | "Y" | "N"` tri-state onto the wire's Y/N, omitting it entirely
 * while still unanswered.
 * @param {string} value - The tri-state value.
 * @returns {"Y" | "N" | undefined} The flag to send, or undefined to omit it.
 */
function toOptionalFlag(value: string): "Y" | "N" | undefined {
  if (value === "Y" || value === "N") {
    return value;
  }
  return undefined;
}

/**
 * Renders a field's value the way the save contract's `answerEntered` /
 * `answerStored` carry it: Y/N for the flag rows, the raw string or number
 * otherwise, and "" for anything unanswered.
 * @param {ResidenceVerifiedFields[ResidenceQuestionFieldKey]} value - The field's value.
 * @returns {string} The wire representation.
 */
function toAnswerText(value: ResidenceVerifiedFields[ResidenceQuestionFieldKey]): string {
  if (typeof value === "boolean") {
    return toYesNo(value);
  }
  return value === "" ? "" : String(value);
}

/**
 * Builds the per-question `verificationAnswers[]`, one entry per field that
 * carries a CAPS2 verification question. Mirrors the employment script, which
 * sends every question in the catalog rather than only the changed ones, so
 * the saved history entry is a complete record of the call.
 * @param {ResidenceVerifiedFields} verified - The "Verified Information" values.
 * @param {ResidenceVerifiedFields} saved - The "Saved Information" values.
 * @returns {ResidenceCallScriptVerificationAnswer[]} One answer per question field.
 */
export function buildResidenceVerificationAnswers(
  verified: ResidenceVerifiedFields,
  saved: ResidenceVerifiedFields
): ResidenceCallScriptVerificationAnswer[] {
  const keys = Object.keys(RESIDENCE_CALL_SCRIPT_QUESTION_TEXT) as ResidenceQuestionFieldKey[];
  return keys.map((key) => {
    const answerEntered = toAnswerText(verified[key]);
    const answerStored = toAnswerText(saved[key]);
    return {
      questionText: RESIDENCE_CALL_SCRIPT_QUESTION_TEXT[key],
      answerEntered,
      answerStored,
      isAnswerMatch: toYesNo(answerEntered === answerStored),
    };
  });
}

/**
 * Builds the residence Call Script save body from the modal's live state.
 * @param {ResidenceCallScriptSaveContext} context - The modal's captured values plus the ids it needs.
 * @returns {ResidenceCallScriptSaveBody} The POST body.
 */
export function buildResidenceCallScriptSaveBody(context: ResidenceCallScriptSaveContext): ResidenceCallScriptSaveBody {
  const { verifiedFields: verified, savedFields: saved } = context;
  return {
    applicantRole: context.personPath === "driver" ? "DRIVER" : "SECOND_SIGNER",
    applicantAddressPk: context.applicantAddressPk,
    phoneNumberDialed: verified.phoneNumberDialed.replaceAll(/\D/gu, ""),
    isPhoneNoBad: toYesNo(verified.wasOriginalNumberBad),
    dateOfBirth: mergeOptionalString(verified.dateOfBirth, saved.dateOfBirth),
    primaryNumber: mergeOptionalString(verified.primaryNumber, saved.primaryNumber),
    secondaryNumber: mergeOptionalString(verified.secondaryNumber, saved.secondaryNumber),
    isDoNotContactPrimary: toYesNo(context.doNotContactPrimary),
    isEContractConsent: toOptionalFlag(verified.consentToSignElectronically),
    isSignedCopyProvided: toOptionalFlag(verified.signedCopyProvided),
    physicalStreetNumber: mergeOptionalString(verified.physicalStreetNumber, saved.physicalStreetNumber),
    physicalStreetName: mergeOptionalString(verified.physicalStreetName, saved.physicalStreetName),
    physicalApartmentNumber: mergeOptionalString(verified.physicalApartmentNumber, saved.physicalApartmentNumber),
    physicalCity: mergeOptionalString(verified.physicalCity, saved.physicalCity),
    physicalState: mergeOptionalString(verified.physicalState, saved.physicalState),
    physicalZip: mergeOptionalString(verified.physicalZip, saved.physicalZip),
    mailingStreetNumber: mergeOptionalString(verified.mailingStreetNumber, saved.mailingStreetNumber),
    mailingStreetName: mergeOptionalString(verified.mailingStreetName, saved.mailingStreetName),
    mailingApartmentNumber: mergeOptionalString(verified.mailingApartmentNumber, saved.mailingApartmentNumber),
    mailingCity: mergeOptionalString(verified.mailingCity, saved.mailingCity),
    mailingState: mergeOptionalString(verified.mailingState, saved.mailingState),
    mailingZip: mergeOptionalString(verified.mailingZip, saved.mailingZip),
    whoIsPrimaryDriver: mergeOptionalString(verified.whoIsPrimaryDriver, saved.whoIsPrimaryDriver),
    isTradeIn: toOptionalFlag(verified.tradeIn),
    totalCashAndTradeInPaidOnDelivery: mergeOptionalNumber(verified.totalCashAndTradeIn, saved.totalCashAndTradeIn),
    isAnyAmountStillOwed: toOptionalFlag(verified.anyAmountStillOwed),
    remainingAmountDueDate: mergeOptionalString(verified.whenIsRemainingDue, saved.whenIsRemainingDue),
    grossTradeInAmount: mergeOptionalNumber(verified.grossTradeInAmount, saved.grossTradeInAmount),
    tradeInPayoffAmount: mergeOptionalNumber(verified.tradeInPayoffAmount, saved.tradeInPayoffAmount),
    cashDownPaidOnDelivery: mergeOptionalNumber(verified.cashDownPaidOnDelivery, saved.cashDownPaidOnDelivery),
    amountDueAfterDelivery: mergeOptionalNumber(verified.amountDueAfterDelivery, saved.amountDueAfterDelivery),
    finalAmountDueDate: mergeOptionalString(verified.whenIsFinalAmountDue, saved.whenIsFinalAmountDue),
    noteText: context.note,
    verificationAnswers: buildResidenceVerificationAnswers(verified, saved),
  };
}
